import _ from 'lodash';

import { DROP_ZONE_KEYS } from '../data/goblinGourmetConstants';

// generates unique id for tile
const getTileId = text => _.uniqueId(text);

// creates basic tile object
export const getTile = (text, props = {}) => ({
  id: getTileId(text),
  text,
  ...props,
});

// returns same tray tiles collection, but distributed between left and right trays
export const getDistributedTray = (trayCollection) => {
  const cutBorder = Math.round(trayCollection.length / 2);

  return trayCollection.map((tile, index) => ({
    ...tile,
    // first half goes to left, second - to right
    dropZoneKey: index < cutBorder ? DROP_ZONE_KEYS.LEFT_TRAY : DROP_ZONE_KEYS.RIGHT_TRAY,
  }));
};

// calculates collection weight: sum of lengths of all tiles
export const getCollectionWeight = tiles => tiles.reduce((weight, tile) => weight + tile.text.length, 0);

export const getTrayPartitionWeight = (tiles, tray) => {
  const [leftTiles, rightTiles] =
    _.partition(tiles.filter(tile => tray.includes(tile.id)), tile => tile.dropZoneKey === DROP_ZONE_KEYS.LEFT_TRAY);

  return [
    getCollectionWeight(leftTiles),
    getCollectionWeight(rightTiles),
  ];
};

// calculates which tray (left or right) should new tile be added to
export const getEasiestDropZoneKey = (tiles, tray) => {
  const [leftTrayWeight, rightTrayWeight] = getTrayPartitionWeight(tiles, tray);

  return leftTrayWeight > rightTrayWeight ? DROP_ZONE_KEYS.RIGHT_TRAY : DROP_ZONE_KEYS.LEFT_TRAY;
};

const getTilesDiff = (mainTile, secondaryTile) => {
  const result = [];

  const secondaryTileLetters = secondaryTile.split('');

  mainTile.split('').forEach((letter) => {
    if (!secondaryTileLetters.includes(letter)) {
      result.push(letter);
    } else {
      secondaryTileLetters[secondaryTileLetters.indexOf(letter)] = '*';
    }
  });

  return result.join('');
};

const getTilesIntersection = (mainTile, secondaryTile) => {
  const result = [];

  secondaryTile.split('').forEach((letter) => {
    if (mainTile.includes(letter)) {
      result.push(letter);
    }
  });

  return result.join('');
};

export const getSecondAttemptTiles = (state, deskTiles) => {
  const desk = [];
  const tray = [...state.tray];

  // initially we have target word there, e.g. "grape"
  // then we kind of deduct useful desk tiles letters from it
  // e.g. if useful part of 1st desk tile is "gr", we'll have "ape" there
  // then e.g. if useful part of 2nd desk tile is "ap", we'll have "e" there and so on
  let targetWordRemainder = state.targetWord;

  // divide all desk tiles into 2 categories:
  // 1) tiles which target word fully includes (syllable tiles): e.g. "gr" and "ape" tile for target word "grape"
  // 2) rest of tiles: e.g. "geo", which has useful letters "g" and "e" or "zoo", which has no useful letters
  const [syllableTiles, otherTiles] = _.partition(deskTiles, (tile => targetWordRemainder.includes(tile.text)));

  // go through all syllable tiles
  syllableTiles.forEach((tile) => {
    // and deduct every such tile from target word
    // e.g. 1st tile is "gr", so "grape" - "gr" = "ape"
    // e.g. 2nd tile is "ap", so "ape" - "ap" = "e"
    targetWordRemainder = getTilesDiff(targetWordRemainder, tile.text);
    // and add it on the desk
    desk.push(tile.id);
  });

  // now go through rest of tiles and pick only those where tile contains at least 1 letter from target word (what we have remaining from it)
  otherTiles.forEach((tile) => {
    // get intersection between target word remainder and tile, e.g. "grape" + "map" = "ap"
    const intersection = getTilesIntersection(targetWordRemainder, tile.text);

    // so if there is some intersection
    if (intersection) {
      // we deduct this intersection from target word remainder:
      // e.g. intersection between "ape" and "map" is "ap", so diff between "ape" and "ap" is "e"
      targetWordRemainder = getTilesDiff(targetWordRemainder, intersection);
      // and add it on the desk
      desk.push(tile.id);
    } else {
      tray.push(tile.id);
    }
  });

  return {
    desk,
    tray,
  };
};
